/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components\CertLetsEncryptBox.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Let's Encrypt configuration box with Multi-SAN & DNS-01 support.
────────────────────────────────────────────────────────────────*/

import { Lock } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { CertificateFormData, ACME_CHALLENGE_OPTIONS } from '../types';

interface Props {
    formData: CertificateFormData;
    errors: Record<string, string>;
    onChange: (field: keyof CertificateFormData, value: CertificateFormData[keyof CertificateFormData]) => void;
    commonName?: string;
}

export function CertLetsEncryptBox({ formData, errors, onChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('certificates.sections.letsEncrypt')} icon={<Lock className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                <SimpleTooltip content={t('certificates.tooltips.acmeEmail')} title={t('certificates.fields.acmeEmail')} position="top" maxWidth="300px">
                    <Label htmlFor="acmeAccountEmail" required className="text-left">
                        {t('certificates.fields.acmeEmail')}
                    </Label>
                </SimpleTooltip>
                <Input
                    id="acmeAccountEmail"
                    type="email"
                    value={formData.acmeAccountEmail}
                    onChange={(e) => onChange('acmeAccountEmail', e.target.value)}
                    placeholder="admin@example.com"
                    error={errors.acmeAccountEmail}
                />

                <SimpleTooltip content={t('certificates.tooltips.challengeType')} title={t('certificates.fields.challengeType')} position="top" maxWidth="300px">
                    <Label htmlFor="acmeChallengeType" className="text-left">
                        {t('certificates.fields.challengeType')}
                    </Label>
                </SimpleTooltip>
                <Select
                    value={formData.acmeChallengeType}
                    onValueChange={(v) => onChange('acmeChallengeType', v)}
                    options={ACME_CHALLENGE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
                />

                <SimpleTooltip content={t('certificates.tooltips.renewBeforeDays')} title={t('certificates.fields.renewBeforeDays')} position="top" maxWidth="300px">
                    <Label htmlFor="renewBeforeDays" className="text-left">
                        {t('certificates.fields.renewBeforeDays')}
                    </Label>
                </SimpleTooltip>
                <Input
                    id="renewBeforeDays"
                    type="number"
                    min={7}
                    max={60}
                    value={formData.renewBeforeDays}
                    onChange={(e) => onChange('renewBeforeDays', parseInt(e.target.value))}
                />

                <SimpleTooltip content={t('certificates.tooltips.autoRenew')} title={t('certificates.fields.autoRenew')} position="top" maxWidth="300px">
                    <Label htmlFor="autoRenew" className="text-left">
                        {t('certificates.fields.autoRenew')}
                    </Label>
                </SimpleTooltip>
                <Toggle
                    id="autoRenew"
                    value={formData.autoRenew}
                    onChange={(v) => onChange('autoRenew', v)}
                />
            </div>
        </FormBox>
    );
}
